import React, { useCallback, useEffect, useState } from "react";
import { ArrowLeft, CalendarDays, Images, MapPin, Mic2, UserRound } from "lucide-react";
import MediaUploader from "../../components/MediaUploader";
import api, { API, formatApiError, thumbUrl } from "../../lib/api";
import { useToast } from "../../lib/toast";

const PAGE_SIZE = 24;
const VIEWS = [
  ["past", "Past events"],
  ["with_media", "Has media"],
  ["all", "All bookings"],
];

function formatEventDate(value) {
  if (!value) return "Date not set";
  const parsed = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function mediaSummary(booking) {
  const parts = [];
  if (booking.photo_count) parts.push(`${booking.photo_count} photo${booking.photo_count === 1 ? "" : "s"}`);
  if (booking.video_count) parts.push(`${booking.video_count} video${booking.video_count === 1 ? "" : "s"}`);
  return parts.length ? parts.join(" · ") : "No media yet";
}

export default function AdminEventMedia() {
  const toast = useToast();
  const [bookings, setBookings] = useState([]);
  const [counts, setCounts] = useState({});
  const [view, setView] = useState("past");
  const [bookingPage, setBookingPage] = useState(1);
  const [bookingPages, setBookingPages] = useState(1);
  const [bookingSearch, setBookingSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const selectedBookingId = selectedBooking?.id || "";
  const [media, setMedia] = useState([]);
  const [details, setDetails] = useState("");
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [mediaTitle, setMediaTitle] = useState("");
  const [replacingMediaId, setReplacingMediaId] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  // Debounce the search box so typing does not fire a request per key.
  useEffect(() => {
    const timer = setTimeout(() => { setSearchQuery(bookingSearch.trim()); setBookingPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [bookingSearch]);

  useEffect(() => {
    let active = true;
    setLoadingBookings(true);
    api.get("/admin/event-media/bookings", { params: { view, q: searchQuery, page: bookingPage, limit: PAGE_SIZE } })
      .then(({ data }) => {
        if (!active) return;
        setBookings(Array.isArray(data?.items) ? data.items : []);
        setCounts(data?.counts || {});
        setBookingPages(Math.max(1, Number(data?.pages || 1)));
      })
      .catch((error) => {
        if (active) toast(`Could not load bookings: ${formatApiError(error)}`, "error");
      })
      .finally(() => { if (active) setLoadingBookings(false); });
    return () => { active = false; };
  }, [view, searchQuery, bookingPage, reloadKey, toast]);

  const loadMedia = useCallback(async () => {
    if (!selectedBookingId) {
      setMedia([]);
      setDetails("");
      return;
    }
    setLoadingMedia(true);
    try {
      const { data } = await api.get(`/bookings/${encodeURIComponent(selectedBookingId)}/event-media`);
      setMedia(data?.media || []);
      setDetails(data?.details || "");
    } catch (error) {
      toast(`Could not load event media: ${formatApiError(error)}`, "error");
    } finally {
      setLoadingMedia(false);
    }
  }, [selectedBookingId, toast]);

  useEffect(() => { loadMedia(); }, [loadMedia]);

  // Going back refreshes the list so its media counts are current.
  const backToBookings = () => {
    setSelectedBooking(null);
    setReplacingMediaId("");
    setMediaTitle("");
    setReloadKey((key) => key + 1);
  };

  const uploadFinished = async () => {
    setReplacingMediaId("");
    setMediaTitle("");
    toast("Event media uploaded.", "success");
    await loadMedia();
  };

  const togglePublished = async (item) => {
    const published = !item.published;
    try {
      await api.patch(
        `/admin/bookings/${encodeURIComponent(selectedBookingId)}/event-media/${encodeURIComponent(item.id)}`,
        { published },
      );
      toast(published ? "Media published to the Events Gallery." : "Media removed from the Events Gallery.", "success");
      await loadMedia();
    } catch (error) {
      toast(formatApiError(error), "error");
    }
  };

  const renameMedia = async (item) => {
    const title = window.prompt("Event media title", item.title || "");
    if (title === null || !title.trim()) return;
    try {
      await api.patch(
        `/admin/bookings/${encodeURIComponent(selectedBookingId)}/event-media/${encodeURIComponent(item.id)}`,
        { title: title.trim() },
      );
      toast("Event media title updated.", "success");
      await loadMedia();
    } catch (error) {
      toast(formatApiError(error), "error");
    }
  };

  const deleteMedia = async (item) => {
    if (!window.confirm(`Delete "${item.title}" from this booking?`)) return;
    try {
      await api.delete(
        `/admin/bookings/${encodeURIComponent(selectedBookingId)}/event-media/${encodeURIComponent(item.id)}`,
      );
      toast("Event media deleted.", "success");
      await loadMedia();
    } catch (error) {
      toast(formatApiError(error), "error");
    }
  };

  return (
    <div className="pad-24" data-testid="admin-event-media">
      <h1 className="font-serif fs-24 fw-700 mb-8">Event Media</h1>
      <p className="text-muted fs-13 mb-16">
        Add photos and videos from past events. Published files appear in the public Events Gallery.
      </p>

      {!selectedBookingId && (
        <section className="card card-pad mb-16" aria-label="Choose an event">
          <div className="em-toolbar">
            <div className="em-views" role="tablist" aria-label="Which bookings to show">
              {VIEWS.map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={view === id}
                  className={`rail-chip ${view === id ? "active" : ""}`}
                  onClick={() => { setView(id); setBookingPage(1); }}
                  data-testid={`admin-event-media-view-${id}`}
                >
                  {label}{counts[id] !== undefined && <span className="em-view-count">{counts[id]}</span>}
                </button>
              ))}
            </div>
            <input
              id="admin-event-media-search"
              className="field-input em-search"
              type="search"
              placeholder="Search event, city, artist, customer or booking ID"
              aria-label="Search bookings"
              value={bookingSearch}
              onChange={(event) => setBookingSearch(event.target.value)}
              data-testid="admin-event-media-search"
            />
          </div>

          {loadingBookings && bookings.length === 0 ? (
            <p className="text-muted fs-13">Loading events…</p>
          ) : bookings.length === 0 ? (
            <p className="text-muted fs-13">
              {searchQuery
                ? "No bookings match your search."
                : view === "past" ? "No past events yet." : view === "with_media" ? "No booking has event media yet." : "No bookings yet."}
            </p>
          ) : (
            <div className="em-booking-grid" style={{ opacity: loadingBookings ? 0.6 : 1 }}>
              {bookings.map((booking) => (
                <button
                  key={booking.id}
                  type="button"
                  className="em-booking-card"
                  onClick={() => setSelectedBooking(booking)}
                  data-testid={`admin-event-media-booking-${booking.id}`}
                >
                  <span className="em-booking-cover">
                    {booking.cover_media_id
                      ? <img src={thumbUrl(booking.cover_media_id)} alt="" loading="lazy" />
                      : <Images size={26} aria-hidden="true" />}
                    {booking.published_count > 0 && <span className="em-booking-badge">{booking.published_count} in gallery</span>}
                  </span>
                  <span className="em-booking-body">
                    <span className="em-booking-title">{booking.event_type}</span>
                    <span className="em-booking-meta">
                      <CalendarDays size={13} aria-hidden="true" /> {formatEventDate(booking.event_date)}
                      {booking.city && <><MapPin size={13} aria-hidden="true" /> {booking.city}</>}
                    </span>
                    <span className="em-booking-meta"><Mic2 size={13} aria-hidden="true" /> {booking.artist_name}</span>
                    <span className="em-booking-meta"><UserRound size={13} aria-hidden="true" /> {booking.customer_name}</span>
                    <span className="em-booking-foot">
                      <span className={booking.photo_count || booking.video_count ? "em-has-media" : "text-muted"}>{mediaSummary(booking)}</span>
                      <span className="text-muted">{booking.ref}</span>
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {bookingPages > 1 && (
            <div className="flex-between mt-16">
              <span className="text-muted fs-12">Page {bookingPage} of {bookingPages}</span>
              <div style={{ display: "flex", gap: "var(--space-2)" }}>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setBookingPage((page) => Math.max(1, page - 1))} disabled={bookingPage <= 1 || loadingBookings}>
                  Previous
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setBookingPage((page) => Math.min(bookingPages, page + 1))} disabled={bookingPage >= bookingPages || loadingBookings}>
                  Next
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {selectedBookingId && (
        <>
          <section className="card card-pad mb-16">
            <button type="button" className="btn btn-ghost btn-sm mb-12" onClick={backToBookings} data-testid="admin-event-media-back">
              <ArrowLeft size={14} aria-hidden="true" /> All events
            </button>
            <h2 className="font-serif fs-20 fw-700 mb-4">{selectedBooking.event_type}</h2>
            <div className="em-booking-meta mb-12" style={{ flexWrap: "wrap" }}>
              <CalendarDays size={14} aria-hidden="true" /> {formatEventDate(selectedBooking.event_date)}
              {selectedBooking.city && <><MapPin size={14} aria-hidden="true" /> {selectedBooking.city}</>}
              <Mic2 size={14} aria-hidden="true" /> {selectedBooking.artist_name}
              <UserRound size={14} aria-hidden="true" /> {selectedBooking.customer_name}
              <span className="text-muted">· {selectedBooking.ref}</span>
            </div>
            {details && <p className="text-muted fs-13 mb-12">{details}</p>}
            <h2 className="fs-16 fw-700 mb-8">Upload event photos or videos</h2>
            <p className="text-muted fs-12 mb-12">New uploads stay private until you publish them to the Events Gallery.</p>
            <label className="field-label" htmlFor="admin-event-media-title">File title (optional)</label>
            <input
              id="admin-event-media-title"
              className="field-input mb-12"
              value={mediaTitle}
              maxLength={200}
              onChange={(event) => setMediaTitle(event.target.value)}
              placeholder="For example: Stage setup or Event highlights"
              data-testid="admin-event-media-title"
            />
            <MediaUploader
              key={selectedBookingId}
              type="booking_event"
              accept="image/*,video/*"
              maxFiles={5}
              bookingId={selectedBookingId}
              title={mediaTitle.trim() || undefined}
              onComplete={uploadFinished}
            />
          </section>

          <section className="card card-pad" aria-label="Booking event media">
            <div className="flex-between mb-12">
              <h2 className="fs-16 fw-700"><Images size={17} style={{ verticalAlign: "middle", marginRight: 8 }} />Uploaded media</h2>
              <button type="button" className="btn btn-ghost btn-sm" onClick={loadMedia} disabled={loadingMedia}>
                Refresh
              </button>
            </div>
            {loadingMedia ? (
              <p className="text-muted fs-13">Loading event media…</p>
            ) : media.length === 0 ? (
              <p className="text-muted fs-13">No event photos or videos have been added to this booking.</p>
            ) : (
              <div className="bd-event-media-grid">
                {media.map((item) => {
                  const isVideo = item.mime?.startsWith("video/");
                  const isImage = item.mime?.startsWith("image/");
                  const fileUrl = `${API}/media/${encodeURIComponent(item.id)}/file`;
                  return (
                    <article className="bd-event-media-item" key={item.id} data-testid={`admin-event-media-${item.id}`}>
                      <div className="bd-event-media-preview">
                        {isVideo ? (
                          <video src={fileUrl} controls preload="metadata" aria-label={item.title} />
                        ) : isImage ? (
                          <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                            <img src={thumbUrl(item.id)} alt={item.title} loading="lazy" />
                          </a>
                        ) : (
                          <a className="bd-event-media-download" href={fileUrl} target="_blank" rel="noopener noreferrer">Open file</a>
                        )}
                      </div>
                      <div className="bd-event-media-item-info">
                        <strong title={item.title}>{item.title}</strong>
                        <span>{isVideo ? "Video" : isImage ? "Photo" : "File"}</span>
                      </div>
                      <div className="bd-event-media-actions">
                        <button
                          type="button"
                          className={`btn btn-xs ${item.published ? "btn-gold" : "btn-ghost"}`}
                          onClick={() => togglePublished(item)}
                        >
                          {item.published ? "Published" : "Publish"}
                        </button>
                        <button type="button" className="btn btn-ghost btn-xs" onClick={() => renameMedia(item)}>Edit title</button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-xs"
                          onClick={() => setReplacingMediaId(replacingMediaId === item.id ? "" : item.id)}
                        >
                          {replacingMediaId === item.id ? "Cancel replace" : "Replace"}
                        </button>
                        <button type="button" className="btn btn-red btn-xs" onClick={() => deleteMedia(item)}>Delete</button>
                      </div>
                      {replacingMediaId === item.id && (
                        <div className="bd-event-media-replace">
                          <p>Replacing a published file keeps it published; private files remain private.</p>
                          <MediaUploader
                            key={`replace-${selectedBookingId}-${item.id}`}
                            type="booking_event"
                            accept="image/*,video/*"
                            maxFiles={1}
                            bookingId={selectedBookingId}
                            replaceMediaId={item.id}
                            title={item.title}
                            onComplete={uploadFinished}
                          />
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
